export type ProdutoConversao = {
  unidade: string
  gramas_por_unidade?: number | null
  unidades_por_embalagem?: number | null
  gramas_por_item?: number | null
  gramas_por_ml?: number | null
  ml_por_unidade?: number | null
}

export const UNIDADES_COMERCIAIS = ['kg', 'g', 'un', 'ml', 'maço'] as const

export function isUnidadeKg(unidade: string): boolean {
  const u = unidade.trim().toLowerCase()
  return u === 'kg' || u === 'quilo' || u === 'quilograma'
}

export function isUnidadeGrama(unidade: string): boolean {
  const u = unidade.trim().toLowerCase()
  return u === 'g' || u === 'gr' || u === 'grama' || u === 'gramas'
}

export function isUnidadeMl(unidade: string): boolean {
  const u = unidade.trim().toLowerCase()
  return u === 'ml' || u === 'l' || u === 'lt' || u === 'litro' || u === 'litros'
}

/** UN: embalagem com várias unidades (ex.: cartela de ovos). */
export function isUnidadeEmbalagem(unidade: string): boolean {
  const u = unidade.trim().toLowerCase()
  return u === 'un' || u === 'und' || u === 'unidade' || u === 'unidades'
}

export function isUnidadeMaco(unidade: string): boolean {
  const u = unidade.trim().toLowerCase()
  return u === 'maco' || u === 'maço'
}

export type ModoConversaoUnidade = 'embalagem' | 'maco' | 'direta'

/** embalagem = UN (sempre un × gramas); maco = só quantidade; direta = demais. */
export function modoConversaoUnidade(
  unidade: string,
  _ctx?: { unidades_por_embalagem?: number | null },
): ModoConversaoUnidade {
  if (isUnidadeMaco(unidade)) return 'maco'
  if (isUnidadeEmbalagem(unidade)) return 'embalagem'
  return 'direta'
}

/** @deprecated use isUnidadeEmbalagem */
export function isUnidadeContagem(unidade: string): boolean {
  return isUnidadeEmbalagem(unidade)
}

export function totalGramasEmbalagem(
  unidadesPorEmbalagem: number | null | undefined,
  gramasPorItem: number | null | undefined,
): number | null {
  if (unidadesPorEmbalagem == null || gramasPorItem == null) return null
  if (!(unidadesPorEmbalagem > 0) || !(gramasPorItem > 0)) return null
  return unidadesPorEmbalagem * gramasPorItem
}

export function formatUnidadeExibicao(unidade: string): string {
  const u = unidade.trim().toLowerCase()
  if (u === 'kg') return 'Kg'
  if (isUnidadeGrama(unidade)) return 'g'
  if (u === 'un' || u === 'und' || u === 'unidade') return 'UN'
  if (u === 'ml') return 'ml'
  if (u === 'maco' || u === 'maço') return 'Maço'
  return unidade.trim()
}

/** Sugestão quando ainda não há gramas salvas (1 kg = 1000 g). */
export function sugerirGramasPorUnidade(unidade: string): number | null {
  if (isUnidadeKg(unidade)) return 1000
  if (isUnidadeGrama(unidade)) return 1
  return null
}

/** Gramas equivalentes a 1 unidade de venda do produto. */
export function gramasEquivalenteUnidadeComercial(p: ProdutoConversao): number | null {
  if (isUnidadeEmbalagem(p.unidade)) {
    const total = totalGramasEmbalagem(p.unidades_por_embalagem, p.gramas_por_item)
    if (total != null) return total
  }

  if (p.gramas_por_unidade != null && p.gramas_por_unidade > 0) {
    return p.gramas_por_unidade
  }

  if (isUnidadeMl(p.unidade)) {
    const ml = p.ml_por_unidade
    const gml = p.gramas_por_ml
    if (ml != null && ml > 0 && gml != null && gml > 0) return ml * gml
  }

  return sugerirGramasPorUnidade(p.unidade)
}

export function precoPor100g(valorUnitario: number, gramasPorUnidade: number): number | null {
  if (!Number.isFinite(valorUnitario) || valorUnitario <= 0) return null
  if (!Number.isFinite(gramasPorUnidade) || gramasPorUnidade <= 0) return null
  return (valorUnitario / gramasPorUnidade) * 100
}

export function formatPreco100g(valor: number | null): string {
  if (valor == null || !Number.isFinite(valor)) return '—'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor)
}

export function formatGramas(valor: number | null | undefined): string {
  if (valor == null || !Number.isFinite(valor)) return '—'
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 4,
  }).format(valor)
}

/** Kg equivalentes a 1 unidade comercial do produto (ex.: cartela 1,5 kg). */
export function kgPorUnidadeComercial(p: ProdutoConversao): number | null {
  const gramas = gramasEquivalenteUnidadeComercial(p)
  if (gramas == null || gramas <= 0) return null
  return gramas / 1000
}

/** Converte kg em unidades comerciais fechadas (ex.: 1,2 kg ÷ 1,5 kg/cartela = 0,8). */
export function kgParaUnidadesComerciais(kg: number, p: ProdutoConversao): number | null {
  const kgUn = kgPorUnidadeComercial(p)
  if (kgUn == null || kgUn <= 0 || !Number.isFinite(kg)) return null
  if (kg === 0) return 0
  if (kg < 0) return kg / kgUn
  return kg / kgUn
}

/** Converte unidades comerciais em kg (inverso de kgParaUnidadesComerciais). */
export function unidadesComerciaisParaKg(un: number, p: ProdutoConversao): number | null {
  const kgUn = kgPorUnidadeComercial(p)
  if (kgUn == null || kgUn <= 0 || !Number.isFinite(un)) return null
  return un * kgUn
}

export type DirecaoArredondamentoEmbalagem = 'mais' | 'menos'

/** Embalagens fechadas necessárias (arredonda para cima). */
export function produtosFechadosNecessarios(unidadesExatas: number | null | undefined): number | null {
  if (unidadesExatas == null || !Number.isFinite(unidadesExatas) || unidadesExatas <= 0) return null
  return Math.ceil(unidadesExatas)
}

/** Embalagens fechadas para baixo (floor). Zero se a necessidade for menor que 1 pacote. */
export function produtosFechadosParaBaixo(unidadesExatas: number | null | undefined): number | null {
  if (unidadesExatas == null || !Number.isFinite(unidadesExatas) || unidadesExatas <= 0) return null
  return Math.floor(unidadesExatas)
}

export type OpcaoArredondamentoEmbalagem = {
  un: number
  kg: number
  deltaKg: number
}

export type OpcoesArredondamentoEmbalagem = {
  precisaEscolha: boolean
  unExata: number
  menos: OpcaoArredondamentoEmbalagem
  mais: OpcaoArredondamentoEmbalagem
}

/** Calcula opções para menos (floor) e para mais (ceil). Null se não houver conversão. */
export function opcoesArredondamentoEmbalagem(
  kg: number,
  p: ProdutoConversao | null | undefined,
): OpcoesArredondamentoEmbalagem | null {
  if (!p || !Number.isFinite(kg) || kg <= 0) return null
  const unExata = kgParaUnidadesComerciais(kg, p)
  if (unExata == null) return null
  const unMais = produtosFechadosNecessarios(unExata)
  const unMenos = produtosFechadosParaBaixo(unExata)
  if (unMais == null || unMenos == null) return null
  const kgMais = unidadesComerciaisParaKg(unMais, p)
  const kgMenos = unidadesComerciaisParaKg(unMenos, p)
  if (kgMais == null || kgMenos == null) return null
  const precisaEscolha = Math.abs(unExata - unMais) > 1e-9
  return {
    precisaEscolha,
    unExata,
    menos: { un: unMenos, kg: kgMenos, deltaKg: kgMenos - kg },
    mais: { un: unMais, kg: kgMais, deltaKg: kgMais - kg },
  }
}

/**
 * Arredonda kg para múltiplo de 1 unidade comercial (Kg/g/UN/ml/Maço).
 * `direcao` default: mais (ceil). Sem conversão, devolve o kg original.
 */
export function arredondarKgParaEmbalagensFechadas(
  kg: number,
  p: ProdutoConversao | null | undefined,
  direcao: DirecaoArredondamentoEmbalagem = 'mais',
): number {
  if (!Number.isFinite(kg) || kg <= 0) return Number.isFinite(kg) ? kg : 0
  if (!p) return kg
  const opcoes = opcoesArredondamentoEmbalagem(kg, p)
  if (!opcoes) return kg
  return direcao === 'menos' ? opcoes.menos.kg : opcoes.mais.kg
}

/** True quando o kg não é múltiplo inteiro da unidade comercial (fração de pacote). */
export function kgTemFracaoEmbalagem(
  kg: number,
  p: ProdutoConversao | null | undefined,
): boolean {
  if (!p || !Number.isFinite(kg) || kg <= 0) return false
  const opcoes = opcoesArredondamentoEmbalagem(kg, p)
  return opcoes?.precisaEscolha === true
}

/** Valor proporcional: qtd pedida (kg) × preço por kg do produto comercial. */
export function valorTotalPorKgPedido(
  qtdPedidaKg: number,
  valorUnitario: number,
  p: ProdutoConversao,
): number | null {
  const kgUn = kgPorUnidadeComercial(p)
  if (kgUn == null || kgUn <= 0) return null
  if (!Number.isFinite(qtdPedidaKg) || qtdPedidaKg < 0) return null
  if (!Number.isFinite(valorUnitario) || valorUnitario < 0) return null
  return qtdPedidaKg * (valorUnitario / kgUn)
}

export function textoConversaoGramas(p: ProdutoConversao): string {
  if (isUnidadeEmbalagem(p.unidade)) {
    const qtd = p.unidades_por_embalagem
    const peso = p.gramas_por_item
    const total = totalGramasEmbalagem(qtd, peso)
    if (total != null && qtd != null && peso != null) {
      return `${formatGramas(qtd)} × ${formatGramas(peso)} g = ${formatGramas(total)} g`
    }
  }
  if (isUnidadeMaco(p.unidade) && p.gramas_por_unidade != null && p.gramas_por_unidade > 0) {
    return `${formatGramas(p.gramas_por_unidade)} g`
  }
  return formatGramas(gramasEquivalenteUnidadeComercial(p))
}
